import {
    BaseBoxShapeUtil,
    HTMLContainer,
    RecordProps,
    T,
    TLBaseShape,
    stopEventPropagation,
    TLResizeHandle,
} from 'tldraw'
import {TaskStatusType} from "../types/taskFlow.types";
import CircularProgress from "@mui/material/CircularProgress";
import {TaskLoading} from "../icons";
import {useEditor} from "@tldraw/editor";
import {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";


type IMyEditableShape = TLBaseShape<
    'loading-shape',
    {
        text: string,
        status: string,
        h: number,
        w: number,
        loading: boolean
        taskId: number,
        timeEstimate?: number,
        startTime?: number,
    }
>
function LoadingShapeComponent({ shape }: { shape: IMyEditableShape }) {
    const editor = useEditor();
    const isEditing = editor.getEditingShapeId() === shape.id;

    const [elapsedTime, setElapsedTime] = useState(0);

    useEffect(() => {
        let intervalId: number | null = null;

        if (shape.props.startTime && shape.props.timeEstimate && shape.props.loading) {
            intervalId = window.setInterval(() => {
                const currentTime = Math.floor(Date.now() / 1000);
                const elapsed = currentTime - shape.props.startTime!;
                setElapsedTime(elapsed);
            }, 1000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [shape.props.startTime, shape.props.timeEstimate, shape.props.loading]);

    const { t } = useTranslation();

    const timeFormat = useCallback(() => {
        const minutes = Math.floor(elapsedTime / 60);
        const seconds = elapsedTime % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }, [elapsedTime]);


    return (
        <HTMLContainer
            id={shape.id}
            onPointerDown={isEditing ? stopEventPropagation : undefined}
            style={{
                pointerEvents: isEditing ? 'all' : 'none',
            }}
        >
            <div style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                justifyContent: 'center'
            }}>
                {shape.props.loading && (
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center"
                    }}>
                        <TaskLoading size={50} />
                        {!!shape.props.timeEstimate && shape.props.startTime && (
                            <div style={{color: '#007AFF', paddingTop: 6, fontWeight: 'bold'}}>
                                {timeFormat()} / {shape.props.timeEstimate}s
                            </div>
                        )}
                    </div>
                )}
                {!shape.props.loading && shape.props.text && (
                    <span style={{color: 'red'}}>{
                        (shape.props.text === '暂无任何相关图片'
                        || shape.props.text === 'No related images found') ?
                        t('no_related_images') : t(shape.props.text)
                    }</span>
                )}
            </div>
        </HTMLContainer>
    );
}

export class LoadingShapeUtil extends BaseBoxShapeUtil<IMyEditableShape> {
    static override type = 'loading-shape' as const
    static override props: RecordProps<IMyEditableShape> = {
        text: T.string,
        status: T.string,
        w: T.number,
        h: T.number,
        loading: T.boolean,
        taskId: T.number,
        timeEstimate: T.any,
        startTime: T.any,
    }


    getDefaultProps(): IMyEditableShape['props'] {
        return {
            text: '对【蛇元素】和【新中式】进行发散联想',
            status: '待确认',
            w: 260,
            h: 80,
            loading: true,
            taskId: 0,
            timeEstimate: 0,
            startTime: 0
        }
    }

    // 禁用调整大小功能
    override canResize = (_shape: IMyEditableShape) => {
        return false
    }

    // 隐藏调整大小的控制点
    override hideResizeHandles = (_shape: IMyEditableShape): boolean => {
        return true
    }

    component(shape: IMyEditableShape) {
        return <LoadingShapeComponent shape={shape} />;
    }

    indicator(shape: IMyEditableShape) {
        // 返回固定大小的indicator
        return <rect width={shape.props.w} height={shape.props.h} />
    }
}